import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import AxiosInstance from "../../Service/AxiosInstance";
import { toast } from "react-toastify";

interface CustomerUser {
  ownerId: string; // Changed from id to ownerId
  userName: string;
  count?: number; // Current count of completed tasks
  target?: number; // Target number of tasks
  track?: boolean;
}

export interface Team {
  ownerId: string; // Changed from id to ownerId
  name: string;
  unassignedImages?: number;
  currentUser?: string;
}

export interface AssignRolePayload {
  userId?: string;
  ownerId: string;
  userName: string;
  count?: number; // Current count of completed tasks
  target?: number; // Target number of tasks
  track?: boolean;
  projectId?: string;
  isImageUpload?: boolean;
  imageTarget?: number;
  isAnnotate?: boolean;
  annotationTarget?: number;
  isvalidate?: boolean;
  isTrain?: boolean;
  customerId?: string;
}

export interface RequestAccess {
  projectId: string;
  ownerId: string;
  access: string;
}

export interface UpdateRoleResponse {
  id: string;
  projectId: string;
  project: any;
  isImageUpload: boolean;
  imageTarget: number;
  isAnnotate: boolean;
  annotationTarget: number;
  isvalidator: boolean;
  isTrainer: boolean;
  ownerId: string;
  createdBy: string;
  createdDate: string;
  modifiedBy: string;
  modifiedDate: string;
  customerId: string;
}


interface CustomerUsersState {
  usersList: CustomerUser[];
  imageUploaders: CustomerUser[];
  annotators: CustomerUser[];
  validators: CustomerUser[];
  trainers: CustomerUser[];
  team: Team | null;
  trainValidation: any;
  loading: boolean;
  error: string | null;
}

const initialState: CustomerUsersState = {
  usersList: [],
  imageUploaders: [],
  annotators: [],
  validators: [],
  trainers: [],
  team: null,
  trainValidation: null,
  loading: false,
  error: null,
};

export const fetchCustomerUsers = createAsyncThunk(
  "customerUsers/fetchCustomerUsers",
  async (customerId: string, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.get(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/ActivityLogs/GetAllCustomerUsers?customerId=${customerId}`
      );
      // Transform the response data to use ownerId instead of id
      const transformedData = response.data.map((user: any) => ({
        ownerId: user.id, // Map id to ownerId
        userName: user.userName,
        count: user.count,
        target: user.target,
        track: user.track,
      }));
      return transformedData;
    } catch (error: any) {
      if (typeof error.response.data == "string") {
        toast.error(error.response.data);
      } else {
        toast.error(error.response.data.detail);
      }
      return rejectWithValue(error.response.data);
    }
  }
);

// New GET endpoints
export const fetchImageUploaders = createAsyncThunk(
  "customerUsers/fetchImageUploaders",
  async (projectId: string, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.get(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/ProjectTeam/GetAllImageUploader?projectId=${projectId}`
      );
      return response.data;
    } catch (error: any) {
      const message =
        error.response?.data?.detail ||
        error.response?.data ||
        "Error fetching image uploaders";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const fetchAnnotators = createAsyncThunk(
  "customerUsers/fetchAnnotators",
  async (projectId: string, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.get(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/ProjectTeam/GetAllAnnotator?projectId=${projectId}`
      );
      return response.data;
    } catch (error: any) {
      const message =
        error.response?.data?.detail ||
        error.response?.data ||
        "Error fetching annotators";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const fetchValidators = createAsyncThunk(
  "customerUsers/fetchValidators",
  async (projectId: string, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.get(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/ProjectTeam/GetAllValidator?projectId=${projectId}`
      );
      return response.data;
    } catch (error: any) {
      const message =
        error.response?.data?.detail ||
        error.response?.data ||
        "Error fetching validators";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const fetchTrainers = createAsyncThunk(
  "customerUsers/fetchTrainers",
  async (projectId: string, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.get(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/ProjectTeam/GetAllTrainer?projectId=${projectId}`
      );
      return response.data;
    } catch (error: any) {
      const message =
        error.response?.data?.detail ||
        error.response?.data ||
        "Error fetching trainers";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const fetchProjectTeam = createAsyncThunk(
  "team/fetchProjectTeam",
  async (projectId: string, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.get(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/ProjectTeam/GetTeamProject?projectId=${projectId}`
      );
      return response.data;
    } catch (error: any) {
      const message =
        error.response?.data?.detail ||
        error.response?.data ||
        "Error fetching trainers";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// GET request for TrainValidation
export const fetchTrainValidation = createAsyncThunk(
  "team/fetchTrainValidation",
  async (projectId: string, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.get(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/ProjectTeam/TrainValidation?projectId=${projectId}`
      );
      return response.data;
    } catch (error: any) {
      const message =
        error.response?.data?.detail ||
        error.response?.data ||
        "Error fetching train validation data";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const removeImageAnnotator = createAsyncThunk(
  "customerUsers/removeImageAnnotator",
  async (payload: AssignRolePayload, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.post(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/ProjectTeam/RemoveImageAnnotator`,
        payload
      );
      toast.success("Image annotator removed successfully!");
      return response.data;
    } catch (error: any) {
      const message =
        error.response?.data?.detail ||
        error.response?.data ||
        "Error removing image annotator";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);


export const removeImageUploader = createAsyncThunk(
  "customerUsers/removeImageUploader",
  async (payload: AssignRolePayload, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.post(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/ProjectTeam/RemoveImageUploader`,
        payload
      );
      toast.success("Image uploader removed successfully!");
      return response.data;
    } catch (error: any) {
      const message =
        error.response?.data?.detail ||
        error.response?.data ||
        "Error removing image uploader";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);


export const removeTrainer = createAsyncThunk(
  "customerUsers/removeTrainer",
  async (payload: AssignRolePayload, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.post(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/ProjectTeam/RemoveTrainer`,
        payload
      );
      toast.success("Trainer removed successfully!");
      return response.data;
    } catch (error: any) {
      const message =
        error.response?.data?.detail ||
        error.response?.data ||
        "Error removing trainer";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);


export const addImageUploader = createAsyncThunk(
  "customerUsers/addImageUploader",
  async (payload: AssignRolePayload, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.post(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/ProjectTeam/AddImageUploader`,
        payload
      );
      toast.success("Image uploader added successfully!");
      return response.data;
    } catch (error: any) {
      const message =
        error.response?.data?.detail ||
        error.response?.data ||
        "Error adding image uploader";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const addAnnotator = createAsyncThunk(
  "customerUsers/addAnnotator",
  async (payload: AssignRolePayload, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.post(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/ProjectTeam/AddAnnotator`,
        payload
      );
      toast.success("Annotator added successfully!");
      return response.data;
    } catch (error: any) {
      const message =
        error.response?.data?.detail ||
        error.response?.data ||
        "Error adding annotator";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const addValidator = createAsyncThunk(
  "customerUsers/addValidator",
  async (payload: AssignRolePayload, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.post(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/ProjectTeam/AddValidator`,
        payload
      );
      toast.success("Validator added successfully!");
      return response.data;
    } catch (error: any) {
      const message =
        error.response?.data?.detail ||
        error.response?.data ||
        "Error adding validator";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const addTrainer = createAsyncThunk(
  "customerUsers/addTrainer",
  async (payload: AssignRolePayload, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.post(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/ProjectTeam/AddTrainer`,
        payload
      );
      toast.success("Trainer added successfully!");
      return response.data;
    } catch (error: any) {
      const message =
        error.response?.data?.detail ||
        error.response?.data ||
        "Error adding trainer";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const updateImageUploader = createAsyncThunk(
  "customerUsers/updateImageUploader",
  async (payload: AssignRolePayload[], { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.post(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/ProjectTeam/UpdateImageUploader`,
        payload
      );
      toast.success("Image uploader updated successfully!");
      return response.data;
    } catch (error: any) {
      const message =
        error.response?.data?.detail ||
        error.response?.data ||
        "Error updating image uploader";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const updateAnnotator = createAsyncThunk(
  "customerUsers/updateAnnotator",
  async (payload: AssignRolePayload[], { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.post(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/ProjectTeam/UpdateImageAnnotator`,
        payload
      );
      toast.success("Annotator updated successfully!");
      return response.data;
    } catch (error: any) {
      const message =
        error.response?.data?.detail ||
        error.response?.data ||
        "Error updating annotator";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);


export const updateTrainer = createAsyncThunk(
  "customerUsers/updateTrainer",
  async (payload: AssignRolePayload[], { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.post(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/ProjectTeam/UpdateTrainer`,
        payload
      );
      toast.success("Trainer updated successfully!");
      return response.data;
    } catch (error: any) {
      const message =
        error.response?.data?.detail ||
        error.response?.data ||
        "Error updating trainer";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const requestAccess = createAsyncThunk(
  "customerUsers/requestAccess",
  async (payload: RequestAccess, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.post(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/ProjectTeam/SendRequest`,
        payload
      );
      toast.success("Request mail sent to project creator!");
      return response.data;
    } catch (error: any) {
      const message =
        error.response?.data?.detail ||
        error.response?.data ||
        "Error updating annotator";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

const customerUsersSlice = createSlice({
  name: "customerUsers",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCustomerUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomerUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.usersList = action.payload;
      })
      .addCase(fetchCustomerUsers.rejected, (state, action) => {
        state.loading = false;
        state.usersList = [];
        state.error = action.error.message || "Failed to fetch customer users";
      })

      // Image Uploaders
      .addCase(fetchImageUploaders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchImageUploaders.fulfilled, (state, action) => {
        state.loading = false;
        state.imageUploaders = action.payload;
      })
      .addCase(fetchImageUploaders.rejected, (state, action) => {
        state.loading = false;
        state.imageUploaders = [];
        state.error = action.payload as string;
      })

      // Annotators
      .addCase(fetchAnnotators.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAnnotators.fulfilled, (state, action) => {
        state.loading = false;
        state.annotators = action.payload;
      })
      .addCase(fetchAnnotators.rejected, (state, action) => {
        state.loading = false;
        state.annotators = [];
        state.error = action.payload as string;
      })

      // Validators
      .addCase(fetchValidators.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchValidators.fulfilled, (state, action) => {
        state.loading = false;
        state.validators = action.payload;
      })
      .addCase(fetchValidators.rejected, (state, action) => {
        state.loading = false;
        state.validators = [];
        state.error = action.payload as string;
      })

      // Trainers
      .addCase(fetchTrainers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTrainers.fulfilled, (state, action) => {
        state.loading = false;
        state.trainers = action.payload;
      })
      .addCase(fetchTrainers.rejected, (state, action) => {
        state.loading = false;
        state.trainers = [];
        state.error = action.payload as string;
      })

      // Add roles
      .addCase(addImageUploader.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addImageUploader.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(addImageUploader.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(addAnnotator.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addAnnotator.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(addAnnotator.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(addValidator.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addValidator.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(addValidator.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(addTrainer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addTrainer.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(addTrainer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update Image Uploader
      .addCase(updateImageUploader.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateImageUploader.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateImageUploader.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Update Annotator
      .addCase(updateAnnotator.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAnnotator.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateAnnotator.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateTrainer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTrainer.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateTrainer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchProjectTeam.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjectTeam.fulfilled, (state, action) => {
        state.loading = false;
        state.team = action.payload;
      })
      .addCase(fetchProjectTeam.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // TrainValidation
      .addCase(fetchTrainValidation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTrainValidation.fulfilled, (state, action) => {
        state.loading = false;
        state.trainValidation = action.payload;
      })
      .addCase(fetchTrainValidation.rejected, (state, action) => {
        state.loading = false;
        state.trainValidation = null;
        state.error = action.payload as string;
      });
  },
});

export default customerUsersSlice.reducer;
